import { Component, type MouseEvent, type ReactNode } from 'react';
import { Icon } from '@blueprintjs/core';
import {
  type AxisDomain,
  type AxisScale,
  bisector,
  extent,
  scaleBand,
  type ScaleBand,
  scaleLinear,
  type ScaleOrdinal,
  type ScaleTime,
  scaleUtc,
  ticks,
} from 'd3';
import numbro from 'numbro';

import {
  Area,
  Axis,
  AxisOrientation,
  Bar,
  Chart,
  type DataPoint,
  Dot,
  getDomainMaximum,
  LinePath,
  Marker,
  type MetadataType,
  Mouse,
  resolveDataPoint,
  Responsive,
  timeTickFormatFromDomain,
  Tooltip,
} from '../../shared';
import { Box, Flex } from '../../shared/components';
import { type UsageResponse } from '../api';
import { ChartTypes } from '../model';
import { pluralize } from '../utils';

export type UsageChartProps = UsageResponse &
  Readonly<{
    chartType: ChartTypes;
    tooltipText: string;
    color: ScaleOrdinal<string, string>;
    limit?: number;
    renderToolTip: (metadata: MetadataType) => ReactNode;
    focusedSeriesId?: string;
  }>;

export class UsageChart extends Component<UsageChartProps> {
  private overlayElement: SVGRectElement | null = null;

  // getFocusClass is used to determine if an extra class should be applied to the series.
  private getFocusClass(seriesId: string) {
    if (this.props.focusedSeriesId === null || this.props.focusedSeriesId === undefined) {
      return undefined;
    }
    if (this.props.focusedSeriesId === seriesId) {
      return 'focused';
    }
    return 'diminished';
  }

  private getChartTypeLabel(chartType: ChartTypes): string {
    if (chartType === ChartTypes.LINE) {
      return 'Line';
    }
    if (chartType === ChartTypes.BAR) {
      return 'Bar';
    }
    return 'Area';
  }

  public render() {
    const { metadata, series, chartType, tooltipText, color, renderToolTip, limit } = this.props;

    return (
      <Responsive
        minWidth={640}
        minHeight={480}
        render={({ width, height }) => {
          const margin = { top: 20, right: 20, bottom: 20, left: 35 };
          const xDomain = extent(series, (d) => new Date(d.time)) as [Date, Date];
          const xRange = [margin.left, width - margin.right];
          const yDomain = [0, getDomainMaximum(series) || 0];
          const yRange = [height - margin.bottom, margin.top];
          const xFormat = timeTickFormatFromDomain(xDomain);
          const yFormat = this.abbreviateNumber;

          let xScale: ScaleTime<number, number> | ScaleBand<string>;
          const xScaleLinear = scaleUtc().domain(xDomain).range(xRange);

          const xScaleBand = scaleBand()
            .domain(series.map((d) => d.time.toString()))
            .range([margin.left, width - margin.right])
            .padding(0.1);

          const yScale = scaleLinear().domain(yDomain).range(yRange).nice();

          if (chartType === ChartTypes.BAR) {
            xScale = xScaleBand;
          } else {
            xScale = xScaleLinear;
          }

          // Create adapter functions for datavis components
          const xScaleAdapter = (value: number): number => {
            if (chartType === ChartTypes.BAR) {
              const bandScale = xScale as ScaleBand<string>;
              const result = bandScale(value.toString());
              return result !== undefined ? result : 0;
            }
            const timeScale = xScale as ScaleTime<number, number>;
            return timeScale(new Date(value));
          };

          const yScaleAdapter = (value: number): number => yScale(value);

          // Create axis-compatible scales
          const xAxisScale = (chartType === ChartTypes.BAR ? xScale : xScale) as AxisScale<AxisDomain>;

          const yAxisScale = yScale as AxisScale<AxisDomain>;

          const bisectDate = bisector<DataPoint, Date>((d) => new Date(d.time)).right;
          const yTicks = yDomain[1] < 10! ? ticks(0, yDomain[1], yDomain[1]) : yScale.ticks(10);
          let limits: Array<{ time: number; 0: number }>;
          if (limit) {
            limits = series.map((d) => ({ time: d.time, 0: limit }));
          }

          return (
            <Mouse
              render={({ mouseData, onMove, mouseX, mouseY }) => (
                <>
                  <Chart
                    width={width}
                    height={height}
                    ariaLabel={`${this.getChartTypeLabel(chartType)} chart showing ${tooltipText} over time`}
                    ariaDescription={`A ${chartType.toLowerCase()} chart displaying ${tooltipText} data with ${series.length} data points over time`}
                  >
                    <Axis
                      scale={xAxisScale}
                      orientation={AxisOrientation.BOTTOM}
                      tickFormat={xFormat as (value: AxisDomain, index?: number) => string}
                      ticks={series.length < 10 ? 6 : null}
                      transform={`translate(0, ${height - margin.bottom})`}
                    />
                    <Axis
                      scale={yAxisScale}
                      orientation={AxisOrientation.LEFT}
                      tickFormat={yFormat as (value: AxisDomain, index?: number) => string}
                      tickValues={yTicks as AxisDomain[]}
                      transform={`translate(${margin.left}, 0)`}
                    />
                    {limit && (
                      <>
                        <LinePath
                          stroke="#BFCCD6"
                          data={limits}
                          xScale={xScaleAdapter}
                          yScale={yScaleAdapter}
                          x={(d) => d.time}
                          y={(d) => d[0]}
                        />
                      </>
                    )}
                    {chartType === ChartTypes.LINE &&
                      metadata.map((md: MetadataType, i) => (
                        <LinePath
                          stroke={color(i.toString())}
                          key={i}
                          data={series}
                          xScale={xScaleAdapter}
                          yScale={yScaleAdapter}
                          x={(d) => d.time}
                          y={(d) => d[i]}
                          extraClass={this.getFocusClass(md._id || '')}
                        />
                      ))}
                    {chartType === ChartTypes.BAR &&
                      series.map((d, i) => {
                        const bandScale = xScale as ScaleBand<string>;
                        const xPos = bandScale(d.time.toString()) || 0;
                        const yPos = yScale(d[0]);
                        const barWidth = bandScale.bandwidth();
                        const barHeight = yScale(0) - yPos;

                        return (
                          <Bar
                            key={i}
                            x={xPos}
                            y={yPos}
                            width={barWidth}
                            height={barHeight}
                            innerRef={this.setOverlayRef}
                            className="Bar"
                          />
                        );
                      })}
                    {chartType === ChartTypes.AREA &&
                      metadata.map((_, i: number) => (
                        <Area
                          key={i}
                          data={series}
                          xScale={xScaleAdapter}
                          yScale={yScaleAdapter}
                          height={height - 20}
                          x={(d) => d.time}
                          y={(d) => (!d[1] ? d[0] : d[1] + d[0])}
                        />
                      ))}
                    <Bar
                      x={0}
                      y={0}
                      width={width}
                      height={height}
                      fill="transparent"
                      innerRef={this.setOverlayRef}
                      onMouseLeave={() => onMove({ data: null, mouseX: undefined, mouseY: undefined })}
                      onMouseMove={(event: MouseEvent<SVGRectElement>) => {
                        onMove({
                          data: resolveDataPoint(
                            series,
                            xScaleLinear,
                            bisectDate,
                            (d) => new Date(d.time),
                            this.overlayElement,
                            event,
                          ),
                          mouseX: event.clientX,
                          mouseY: event.clientY,
                        });
                      }}
                    />
                    {mouseData && (
                      <>
                        <Marker
                          x1={xScaleAdapter(mouseData.time)}
                          y1={margin.top}
                          x2={xScaleAdapter(mouseData.time)}
                          y2={height - margin.bottom}
                        />

                        {metadata.map((_, i) => (
                          <Dot key={i} cx={xScaleAdapter(mouseData.time)} cy={yScaleAdapter(mouseData[i])} />
                        ))}
                      </>
                    )}
                  </Chart>
                  {mouseData && mouseX !== undefined && mouseY !== undefined && (
                    <Tooltip key={mouseData.time} top={mouseY} left={mouseX} offsetTop={2} offsetLeft={2}>
                      <strong>{xFormat(new Date(mouseData.time))}</strong>
                      {metadata.map((md: MetadataType, i) => (
                        <Flex key={i} justifyContent="space-between">
                          <Box py={1} pb={2}>
                            <Icon
                              icon="symbol-square"
                              iconSize={16}
                              title={null}
                              color={color(i.toString())}
                              className="Icon"
                            />
                            {yFormat(mouseData[i])} {pluralize(tooltipText, mouseData[i])} {renderToolTip(md)}
                          </Box>
                        </Flex>
                      ))}
                    </Tooltip>
                  )}
                </>
              )}
            />
          );
        }}
      />
    );
  }

  private abbreviateNumber = (value: number) => {
    const formatOptions: { average: boolean; mantissa: number; trimMantissa: boolean } = {
      average: true,
      mantissa: 1,
      trimMantissa: true,
    };
    return numbro(value).format(formatOptions);
  };

  private setOverlayRef = (element: SVGRectElement | null) => {
    this.overlayElement = element;
  };
}
